import { AgentResponse, RichCardData } from './types';
import { conversationContext } from './conversationContext';

type ResponseGenerator = () => AgentResponse;

const responseTemplates: Record<string, ResponseGenerator> = {
  // Weather queries
  weather: () => {
    const weather = conversationContext.getContext().weather;
    return {
      content: `Current weather is ${weather.conditions} with ${weather.temp}°C. ${weather.forecast}. Perfect conditions for checking your crops!`,
      richCard: {
        type: 'weather',
        title: weather.conditions,
        subtitle: `${weather.temp}°C`,
        data: { temp: weather.temp, conditions: weather.conditions, forecast: weather.forecast },
      },
      suggestions: ['Should I water?', 'What about tomorrow?'],
    };
  },

  // Score queries
  score: () => {
    return {
      content: "Your farming score is looking great! You've earned 745 points this month. Keep logging your activities to boost your credit score!",
      suggestions: ['Show my level', 'What affects my score?', 'View leaderboard'],
    };
  },

  // Flock management guidance
  planting: () => {
    return {
      content: 'Based on the current season, this is a good time to start a new broiler batch. Would you like tips on chick placement and brooding?',
      richCard: {
        type: 'crop_tip',
        title: 'Best Practices This Season',
        subtitle: 'Broiler Placement, Biosecurity, Feed Management',
        image: '/placeholder.svg',
        data: { crops: ['Broiler Placement', 'Biosecurity', 'Feed Management'] },
        actions: [{ label: 'Learn More', action: 'show_planting_guide' }],
      },
      suggestions: ['Brooding tips', 'Feed schedule', 'Biosecurity checklist'],
    };
  },

  // Market prices
  market: () => {
    return {
      content: 'Current market prices are favorable for broiler chickens. Live bird prices are up 5% this week!',
      richCard: {
        type: 'market_price',
        title: 'Broiler Chickens',
        subtitle: '৳180/kg live weight',
        data: { commodity: 'Broiler Chickens', price: 180, change: 5, trend: 'up' },
        actions: [{ label: 'View All Prices', action: 'show_market_prices' }],
      },
      suggestions: ['Feed prices', 'Best time to sell', 'Market trends'],
    };
  },

  // Crop tips
  tips: () => {
    const crops = conversationContext.getContext().currentCrops;
    const cropName = crops.length > 0 ? crops[0].name : 'your crops';
    
    return {
      content: `Here are some tips for ${cropName}: 1) Monitor flock health daily, 2) Watch for signs of disease, 3) Maintain proper ventilation. Would you like more detailed guidance?`,
      richCard: {
        type: 'crop_tip',
        title: `Tips for ${cropName}`,
        subtitle: 'Keep your flock healthy',
        image: '/placeholder.svg',
        data: { crop: cropName, tips: ['Monitor health', 'Watch for disease', 'Maintain ventilation'] },
      },
      suggestions: ['Disease prevention', 'Feed guide', 'Harvest timing'],
    };
  },

  // Activity reminders
  activities: () => {
    const tasks = conversationContext.getContext().pendingTasks;
    
    if (tasks.length > 0) {
      return {
        content: `You have ${tasks.length} pending task(s). ${tasks[0].task} is coming up soon!`,
        richCard: {
          type: 'activity_reminder',
          title: tasks[0].task,
          subtitle: `Due: ${tasks[0].dueDate.toLocaleDateString()}`,
          data: { task: tasks[0].task, dueDate: tasks[0].dueDate },
          actions: [{ label: 'Log Now', action: 'log_activity' }],
        },
        suggestions: ['Show all tasks', 'Add task', 'Mark complete'],
      };
    }
    
    return {
      content: "You don't have any pending tasks. Would you like to log a new farming activity?",
      suggestions: ['Log activity', 'Set reminder', 'View history'],
    };
  },

  // General help
  help: () => {
    return {
      content: "I'm here to help with:\n• Weather updates\n• Planting guidance\n• Market prices\n• Activity logging\n• Score tracking\n• Farming tips\n\nWhat would you like to know?",
      suggestions: ['Check weather', 'Log activity', 'Market prices', 'My score'],
    };
  },

  // Default fallback
  default: () => {
    return {
      content: "I'm here to help you with your farming! You can ask me about weather, planting, market prices, or logging activities. What would you like to know?",
      suggestions: ['Weather', 'Plant guide', 'Market prices', 'My activities'],
    };
  },
};

export class CachedResponses {
  getResponse(query: string): AgentResponse | null {
    const lowerQuery = query.toLowerCase();

    // Weather queries
    if (lowerQuery.includes('weather') || lowerQuery.includes('rain') || lowerQuery.includes('sun')) {
      return responseTemplates.weather();
    }

    // Score queries
    if (lowerQuery.includes('score') || lowerQuery.includes('points') || lowerQuery.includes('level')) {
      return responseTemplates.score();
    }

    // Planting queries
    if (lowerQuery.includes('plant') || lowerQuery.includes('sow') || lowerQuery.includes('seed')) {
      return responseTemplates.planting();
    }

    // Market queries
    if (lowerQuery.includes('market') || lowerQuery.includes('price') || lowerQuery.includes('sell')) {
      return responseTemplates.market();
    }

    // Tips queries
    if (lowerQuery.includes('tip') || lowerQuery.includes('advice') || lowerQuery.includes('help') || lowerQuery.includes('how')) {
      return responseTemplates.tips();
    }

    // Activity queries
    if (lowerQuery.includes('activity') || lowerQuery.includes('task') || lowerQuery.includes('reminder')) {
      return responseTemplates.activities();
    }

    // Help queries
    if (lowerQuery.includes('what can you') || lowerQuery.includes('help')) {
      return responseTemplates.help();
    }

    return null;
  }

  getDefaultResponse(): AgentResponse {
    return responseTemplates.default();
  }

  getGreetingResponse(): AgentResponse {
    const summary = conversationContext.getSummary();
    return {
      content: `Hello! ${summary} How can I assist you today?`,
      suggestions: ['Check weather', 'Log activity', 'Market prices', 'Farming tips'],
    };
  }
}

export const cachedResponses = new CachedResponses();
